// Every ownership-filtered custom gallery uses one Swiper navigation owner.
function isVariantSwiperGallery(element) {
  return Boolean(element.closest('media-gallery[data-bm-variant-gallery]'));
}

if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");
        if (!this.elements.thumbnails) return;
        // The custom thumbnail component owns these clicks; do not also
        // attach the legacy scroll-based navigation handler.
        if (isVariantSwiperGallery(this) &&
            this.elements.viewer?.matches("media-gallery-slider")) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500),
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false,
                ),
              );
          });
        if (
          this.dataset.desktopLayout?.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`,
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (isVariantSwiperGallery(this) &&
            typeof this.elements.viewer?.slideToMediaId === "function") {
          this.elements.viewer.slideToMediaId(mediaId);
          return;
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector("[data-media-id]");
        if (!activeMedia) {
          return;
        }
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia?.classList?.add("is-active");

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia &&
            activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`,
            );
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: "smooth" });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`,
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition,
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        if (
          !this.elements?.thumbnails ||
          !this.elements?.thumbnails?.isSlideVisible
        )
          return;
        if (this.elements?.thumbnails?.isSlideVisible(thumbnail, 10)) return;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img",
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position,
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation"),
        );
      }
    },
  );
}

class MediaGallerySlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.thumbnailSlider = null;
  }

  connectedCallback() {
    if (typeof Swiper === "undefined") {
      console.error("Swiper library not loaded");
      return;
    }

    this.initMainSlider();
    if (isVariantSwiperGallery(this) && this.variantPendingMediaId) {
      const mediaId = this.variantPendingMediaId;
      this.variantPendingMediaId = null;
      this.slideToMediaId(mediaId);
    }
  }

  initMainSlider() {
    const swiperContainer = this.querySelector(
      ".media-gallery-slider__swiper-wrapper",
    );

    if (!swiperContainer) return;

    this.swiper = new Swiper(this.querySelector(".swiper") || this, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      wrapperClass: "media-gallery-slider__swiper-wrapper",
      slideClass: "swiper-slide",
      navigation: {
        nextEl: this.querySelector(".swiper-button-next"),
        prevEl: this.querySelector(".swiper-button-prev"),
      },
      watchOverflow: true,
      observer: true,
      observeParents: true,
      on: {
        slideChange: (swiper) => {
          this.onSlideChange(swiper);
        },
        init: (swiper) => {
          this.updateGalleryStatus(swiper);
        },
        slideChangeTransitionEnd: (swiper) => {
          if (isVariantSwiperGallery(this)) this.syncVariantMediaState(swiper);
        },
      },
    });
  }

  setThumbnailSlider(thumbnailSlider) {
    this.thumbnailSlider = thumbnailSlider;
    if (isVariantSwiperGallery(this) && this.swiper) {
      this.syncVariantMediaState(this.swiper);
    }
  }

  syncVariantMediaState(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    const mediaId = activeSlide?.dataset.mediaId;
    if (!mediaId) return;

    Array.from(swiper.slides).forEach((slide) => {
      slide.classList.toggle("is-active", slide === activeSlide);
    });
    this.thumbnailSlider?.setActiveSlide(mediaId);
    this.updateGalleryStatus(swiper);

    if (this.variantActiveMediaId !== mediaId) {
      this.variantActiveMediaId = mediaId;
      this.closest("media-gallery")?.playActiveMedia?.(activeSlide);
    }
  }

  onSlideChange(swiper) {
    const activeSlide = swiper.slides[swiper.activeIndex];
    const mediaId = activeSlide?.dataset.mediaId;

    // Update thumbnail active state from the actual Swiper slide.
    if (isVariantSwiperGallery(this)) {
      this.syncVariantMediaState(swiper);
    } else if (this.thumbnailSlider && mediaId) {
      this.thumbnailSlider.setActiveSlide(mediaId);
    }

    // Update gallery status for screen readers
    this.updateGalleryStatus(swiper);

    // Dispatch custom event
    this.dispatchEvent(
      new CustomEvent("media-gallery:slide-change", {
        bubbles: true,
        detail: {
          activeIndex: swiper.activeIndex,
          mediaId: mediaId,
        },
      }),
    );
  }

  updateGalleryStatus(swiper) {
    const mediaGallery = this.closest("media-gallery");
    if (!mediaGallery) return;

    const statusElement = mediaGallery.querySelector('[role="status"]');
    if (statusElement) {
      const currentSlide = swiper.slides.length ? swiper.activeIndex + 1 : 0;
      const totalSlides = swiper.slides.length;
      statusElement.textContent = `Item ${currentSlide} of ${totalSlides}`;
    }
  }

  slideTo(index) {
    if (this.swiper) {
      this.swiper.slideTo(index);
    }
  }

  slideToMediaId(mediaId) {
    if (!this.swiper) {
      if (isVariantSwiperGallery(this)) this.variantPendingMediaId = mediaId;
      return;
    }

    let slideIndex = Array.from(this.swiper.slides).findIndex(
      (slide) => slide.dataset.mediaId === mediaId,
    );

    if (slideIndex === -1 && isVariantSwiperGallery(this) && this.swiper.slides.length) slideIndex = 0;
    if (slideIndex !== -1) {
      if (isVariantSwiperGallery(this)) {
        // Immediate navigation also settles rapid model/package changes.
        this.swiper.slideTo(slideIndex, 0);
        this.syncVariantMediaState(this.swiper);
      } else {
        this.swiper.slideTo(slideIndex);
      }
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
  }
}

class MediaGalleryThumbnail extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.mainSlider = null;
  }

  connectedCallback() {
    if (typeof Swiper === "undefined") {
      console.error("Swiper library not loaded");
      return;
    }

    this.initThumbnailSlider();
    this.setupClickHandlers();
    this.connectToMainSlider();
  }

  initThumbnailSlider() {
    const swiperContainer = this.querySelector(
      ".media-gallery-thumbnail__swiper-wrapper",
    );

    if (!swiperContainer) return;

    // Initialize thumbnail Swiper - use the custom element as container
    this.swiper = new Swiper(this.querySelector(".swiper") || this, {
      slidesPerView: 4.2,
      spaceBetween: 12,
      freeMode: true,
      watchSlidesProgress: true,
      wrapperClass: "media-gallery-thumbnail__swiper-wrapper",
      slideClass: "swiper-slide",
      observer: true,
      observeParents: true,
      breakpoints: {
        0: {
          slidesPerView: 4.2,
          spaceBetween: 8,
        },
        750: {
          slidesPerView: 6.94,
          spaceBetween: 16,
        },
        990: {
          slidesPerView: 5.27,
          spaceBetween: 16,
        },
      },
    });
  }

  connectToMainSlider() {
    // Find and connect to the main slider
    const mediaGallery = this.closest("media-gallery");
    if (mediaGallery) {
      this.mainSlider = mediaGallery.querySelector("media-gallery-slider");

      // Establish bidirectional connection
      if (
        this.mainSlider &&
        typeof this.mainSlider.setThumbnailSlider === "function"
      ) {
        this.mainSlider.setThumbnailSlider(this);
      }
    }
  }

  setupClickHandlers() {
    // Handle thumbnail clicks
    this.addEventListener("click", (e) => {
      const thumbnail = e.target.closest(".media-gallery-thumbnail__thumbnail");
      if (!thumbnail) return;

      const slideElement = thumbnail.closest("[data-target]");
      if (!slideElement) return;

      const targetMediaId = slideElement.dataset.target;

      // Navigate main slider
      if (this.mainSlider && targetMediaId) {
        this.mainSlider.slideToMediaId(targetMediaId);
      }

      // Update active state immediately for better UX
      this.setActiveSlide(targetMediaId);
    });
  }

  setMainSlider(mainSlider) {
    this.mainSlider = mainSlider;
  }

  setActiveSlide(mediaId) {
    // Remove all active states
    this.querySelectorAll('[aria-current="true"]').forEach((el) => {
      el.setAttribute("aria-current", "false");
    });

    // Set new active state
    const activeSlide = this.querySelector(`[data-target="${mediaId}"]`);
    if (activeSlide) {
      const button = activeSlide.querySelector(
        ".media-gallery-thumbnail__thumbnail",
      );
      if (button) {
        button.setAttribute("aria-current", "true");
      }

      // Scroll thumbnail into view if needed
      if (this.swiper) {
        const slideIndex = Array.from(this.swiper.slides).indexOf(activeSlide);
        if (slideIndex !== -1) {
          if (isVariantSwiperGallery(this)) {
            this.swiper.slideTo(slideIndex, 0);
          } else {
            this.swiper.slideTo(slideIndex);
          }
        }
      }
    }
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy();
      this.swiper = null;
    }
  }
}

// Register custom elements
if (!customElements.get("media-gallery-slider")) {
  customElements.define("media-gallery-slider", MediaGallerySlider);
}

if (!customElements.get("media-gallery-thumbnail")) {
  customElements.define("media-gallery-thumbnail", MediaGalleryThumbnail);
}
